/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 filter chip (design-reference channel chips: 28px pill). Two uses:
 * - a toggle in a chip group: `selected`, `onClick`, optional `count`;
 * - an active filter with an X: `onRemove`.
 *
 * Selected uses the info tint with its darker ink text (AA on the tint).
 *
 * @param {object} props
 * @param {React.ReactNode} props.label
 * @param {boolean} [props.selected=false]
 * @param {() => void} [props.onClick] Renders a toggle button with aria-pressed.
 * @param {number|string} [props.count] Shown after the label in tabular numbers.
 * @param {() => void} [props.onRemove] Adds an X button.
 * @param {string} [props.removeLabel] Accessible name of the X; defaults to "Remove <label>".
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { alpha } from "@mui/material/styles";
import { X } from "lucide-react";
import { v2 as t, v2Tones, V2_FONT_FAMILY } from "styles/tokens/v2";

const tone = (selected) =>
  selected
    ? { bgcolor: v2Tones.info.bg, color: v2Tones.info.fg, borderColor: t.primary }
    : { bgcolor: t.surface, color: t.text2, borderColor: t.border2 };

export default function FilterChip({ label, selected, onClick, count, onRemove, removeLabel }) {
  const colours = tone(selected);
  const shell = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    height: 28,
    pl: 1.25,
    pr: onRemove ? 0.5 : 1.25,
    borderRadius: "14px",
    border: "1px solid",
    fontFamily: V2_FONT_FAMILY,
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    ...colours,
  };
  const body = (
    <>
      <span>{label}</span>
      {count !== undefined && count !== null && count !== "" && (
        <Box component="span" sx={{ fontWeight: 500, opacity: 0.85, fontVariantNumeric: "tabular-nums" }}>{count}</Box>
      )}
    </>
  );
  const remove = onRemove ? (
    <ButtonBase
      type="button"
      aria-label={removeLabel || `Remove ${typeof label === "string" ? label : "filter"}`}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      sx={{
        width: 20,
        height: 20,
        borderRadius: "10px",
        color: "inherit",
        "&:hover": { bgcolor: selected ? alpha(v2Tones.info.fg, 0.12) : t.hover },
        "&.Mui-focusVisible": { outline: `2px solid ${t.link}` },
      }}
    >
      <X size={13} aria-hidden="true" />
    </ButtonBase>
  ) : null;

  if (onClick) {
    return (
      <Box sx={{ ...shell, p: 0, overflow: "hidden" }}>
        <ButtonBase
          type="button"
          aria-pressed={selected}
          onClick={onClick}
          sx={{
            height: "100%",
            gap: 0.75,
            pl: 1.25,
            pr: onRemove ? 0.5 : 1.25,
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: "inherit",
            color: "inherit",
            "&:hover": { bgcolor: selected ? v2Tones.info.bg : t.hover },
            "&.Mui-focusVisible": { outline: `2px solid ${t.link}`, outlineOffset: -2 },
          }}
        >
          {body}
        </ButtonBase>
        {remove && <Box sx={{ pr: 0.5, display: "inline-flex" }}>{remove}</Box>}
      </Box>
    );
  }

  return (
    <Box component="span" sx={shell}>
      {body}
      {remove}
    </Box>
  );
}

FilterChip.propTypes = {
  label: PropTypes.node.isRequired,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onRemove: PropTypes.func,
  removeLabel: PropTypes.string,
};

FilterChip.defaultProps = {
  selected: false,
  onClick: undefined,
  count: undefined,
  onRemove: undefined,
  removeLabel: undefined,
};
